import * as React from "react"
import { Link } from "react-router"
import { ArrowDownRight, ArrowUpRight, Minus, RotateCw, type LucideIcon } from "lucide-react"
import { useLingui } from "@lingui/react/macro"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { FormulaTip } from "@/components/ui/formula-tip"
import { useSlowLoading } from "@/hooks/useSlowLoading"
import type { StatusTone } from "@/components/ui/status-badge"

export type StatDeltaDirection = "up" | "down" | "flat"

export interface StatDelta {
  /** Display value including sign and unit, e.g. "+3" or "-12 %". */
  value: React.ReactNode
  direction: StatDeltaDirection
  /** Overrides the default reading (up = success, down = danger) for metrics where up is bad. */
  tone?: StatusTone
}

export interface StatCardProps {
  label: React.ReactNode
  value: React.ReactNode
  hint?: React.ReactNode
  delta?: StatDelta
  /** Adds a tone stripe on the leading edge and colours the icon. */
  tone?: StatusTone
  icon?: LucideIcon
  /**
   * A small figure between the label and the value, e.g. a sparkline. Rendered
   * inside a span, so it must be phrasing content (an inline <svg> is).
   */
  visual?: React.ReactNode
  /** Makes the whole card a link (react-router). */
  href?: string
  /** Makes the whole card a button. Ignored when `href` is set. */
  onClick?: () => void
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
  /** What the figure counts, as the card's native tooltip. */
  title?: string
  /**
   * How the figure is calculated. Shows an info icon at the top right that
   * opens it on hover, click and keyboard focus. The icon sits beside the card,
   * not inside it, so a card that is a link or a button stays valid.
   */
  info?: React.ReactNode
  className?: string
}

const toneText: Record<StatusTone, string> = {
  success: "text-success-on-surface",
  warning: "text-warning-on-surface",
  danger: "text-destructive-on-surface",
  info: "text-info-on-surface",
  neutral: "text-muted-foreground",
  accent: "text-primary",
}

const toneStripe: Record<StatusTone, string> = {
  success: "bg-success-on-surface",
  warning: "bg-warning-on-surface",
  danger: "bg-destructive-on-surface",
  info: "bg-info-on-surface",
  neutral: "bg-border",
  accent: "bg-primary",
}

const deltaIcon: Record<StatDeltaDirection, LucideIcon> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: Minus,
}

const defaultDeltaTone: Record<StatDeltaDirection, StatusTone> = {
  up: "success",
  down: "danger",
  flat: "neutral",
}

/** The delta of a StatCard, also for a figure in running text. */
function DeltaBadge({ delta }: { delta: StatDelta }) {
  const { t } = useLingui()
  const Icon = deltaIcon[delta.direction]
  const directionLabel = {
    up: t({ id: "ui.statCard.delta.up", message: "Increase" }),
    down: t({ id: "ui.statCard.delta.down", message: "Decrease" }),
    flat: t({ id: "ui.statCard.delta.flat", message: "No change" }),
  }[delta.direction]

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-sm font-medium tabular-nums",
        toneText[delta.tone ?? defaultDeltaTone[delta.direction]]
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="sr-only">{directionLabel}</span>
      {delta.value}
    </span>
  )
}

/**
 * A single figure on a dashboard. Label on top, value pinned to the bottom so a
 * row of cards lines up whatever the label length; loading and error states keep
 * the same height so the grid does not jump.
 */
function StatCard({
  label,
  value,
  hint,
  delta,
  tone,
  icon: Icon,
  visual,
  href,
  onClick,
  isLoading = false,
  isError = false,
  onRetry,
  title,
  info,
  className,
}: StatCardProps) {
  const { t } = useLingui()
  const slow = useSlowLoading(isLoading)
  // A retry button cannot sit inside a link or button, so the error state is never clickable.
  const interactive = !isError && (href !== undefined || onClick !== undefined)

  // Spans only: the body also renders inside a <button>, which allows phrasing content alone.
  const body = (
    <>
      {tone && tone !== "neutral" && (
        <span className={cn("absolute inset-y-0 left-0 w-1", toneStripe[tone])} aria-hidden="true" />
      )}
      <span className={cn("flex items-start justify-between gap-2", info !== undefined && "pr-6")}>
        <span className="text-sm text-muted-foreground">{label}</span>
        {Icon && (
          <Icon
            className={cn("size-4 shrink-0", tone ? toneText[tone] : "text-muted-foreground")}
            aria-hidden="true"
          />
        )}
      </span>
      {visual && !isError && <span className="mt-3 block">{visual}</span>}
      <span className="mt-auto flex min-h-8 flex-wrap items-end gap-x-2 gap-y-1 pt-2">
        {isLoading ? (
          <span className="block h-8 w-20 animate-pulse rounded-md bg-muted" aria-hidden="true" />
        ) : isError ? (
          <>
            <span className="text-2xl font-semibold leading-8 text-muted-foreground" aria-hidden="true">
              –
            </span>
            <span className="sr-only">
              {t({ id: "ui.statCard.error", message: "This figure could not be loaded." })}
            </span>
            {onRetry && (
              <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={onRetry}>
                <RotateCw aria-hidden="true" />
                {t({ id: "common.retry", message: 'Try again' })}
              </Button>
            )}
          </>
        ) : (
          <>
            <span className="text-2xl font-semibold leading-8 tabular-nums">{value}</span>
            {delta && <DeltaBadge delta={delta} />}
          </>
        )}
      </span>
      {slow && (
        <span role="status" className="mt-1 block text-xs text-muted-foreground">
          {t({ id: "common.stillLoading", message: "Still loading…" })}
        </span>
      )}
      {hint && !isError && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
    </>
  )

  const base = cn(
    "relative flex h-full min-h-28 flex-col overflow-hidden rounded-lg border bg-card p-4 text-left text-card-foreground shadow-sm",
    interactive &&
      "transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    className
  )

  let card: React.ReactElement
  if (interactive && href !== undefined) {
    card = (
      <Link to={href} className={base} title={title} aria-busy={isLoading || undefined}>
        {body}
      </Link>
    )
  } else if (interactive) {
    card = (
      <button type="button" onClick={onClick} className={cn(base, "w-full")} title={title} aria-busy={isLoading || undefined}>
        {body}
      </button>
    )
  } else {
    card = (
      <div className={base} title={title} aria-busy={isLoading || undefined}>
        {body}
      </div>
    )
  }
  if (info === undefined) return card
  return (
    <div className="relative h-full">
      {card}
      <FormulaTip content={info} className="absolute right-3 top-4" data-testid="stat-card-info" />
    </div>
  )
}

const gridColumns = {
  // At least 180px per card, never more than six.
  default: "grid-cols-[repeat(auto-fit,minmax(max(11.25rem,calc((100%_-_5rem)/6)),1fr))]",
  // Cards that carry a caption and a sparkline: at least 320px, never more than four.
  wide: "grid-cols-[repeat(auto-fill,minmax(max(20rem,calc((100%_-_3rem)/4)),1fr))]",
  // Exactly six cards: one row from 66rem (a 1440px screen), else 3 + 3, 2 + 2 + 2 or one column.
  six: "grid-cols-1 @[24rem]:grid-cols-2 @[36rem]:grid-cols-3 @[66rem]:grid-cols-6",
  // Four cards with a sparkline: one row from 66rem (a 1440px screen), else 2 + 2.
  four: "grid-cols-1 @[30rem]:grid-cols-2 @[66rem]:grid-cols-4",
} as const

/**
 * Responsive row of StatCards. Columns follow the grid's own width (not the
 * viewport): `default` fits at least 180px per card and at most six, `wide`
 * at least 320px and at most four, `six` keeps a row of six cards in one row
 * or in even rows (3 + 3) so no card is left alone, `four` does the same for
 * four (4, else 2 + 2).
 */
function StatGrid({
  className,
  variant = "default",
  children,
}: {
  className?: string
  variant?: keyof typeof gridColumns
  children: React.ReactNode
}) {
  return (
    <div className={cn("@container", className)}>
      <div className={cn("grid gap-4", gridColumns[variant])}>{children}</div>
    </div>
  )
}

export { DeltaBadge, StatCard, StatGrid }
